/**
 * model/beams.ts — Beams: dims, axes, notch, column (PLAN §2.12, kinematic chain).
 *
 * Layer `model/` (SPEC §6): resolves beam specifications (header and joist)
 * starting from the form and the CutTarget. Every function is pure and deterministically
 * testable; works with THREE.Quaternion/THREE.Vector3 (SSOT math =
 * three.js, no linear algebra reimplemented).
 *
 * CHAIN (SPEC §6 point 7): the alphaDeg rotation of the JOIST beam is NOT
 * more calculated here (previously pre-multiplied Rz(alphaDeg) on the
 * quaternion and rotated the center by hand). Now it is the JOINT of the hierarchy:
 * in `resolveScene.ts` the joist beam is the child of the pivot alphaDeg (group
 * with quaternion = Rz(alphaDeg)) — composition occurs in the matrixWorld
 * by three.js. Here we calculate ONLY the fiber axis (nominal center +
 * quaternion axes).
 *
 * Form mapping (SPEC §3.2): `header*` = HEADER beam, `joist*` = JOIST.
 *
 * Constrained 3D configurator: each function is commented for maintainability.*/

import * as THREE from "three";
import type { CutTarget, Vec3 } from "../data/types";
import { axisAngleQuat } from "../transforms/vectors";
import { notchDepthFor } from "./cutTarget";
import type { BeamAxis, BeamDims, BeamOrientation, BeamRole, BeamSpec, NotchSpec } from "./types";

/**
 * Input of `resolveBeam` (PLAN §2.12, adapted to kinematic chain).
 *
 * ADAPTATION: removed `alphaDeg` and `alphaAxisOrigin` (previously they rotated the
 * center/quaternion of hand joist beam). The alphaDeg rotation is now
 * the hierarchy pivot in `resolveScene.ts` (SPEC G6/AC-2).*/
export interface BeamInput {
  /** Beam role: "header" | "joist".*/
  role: BeamRole;
  /** B/H/L dimensions of the beam (B/H from the form, L from the config).*/
  dims: BeamDims;
  /** HEADER beam orientation (SPEC G8); ONLY header uses "column".*/
  orientation: BeamOrientation;
  /** CutTarget fixed from form (config/cutTargets.ts).*/
  cutTarget: CutTarget;
  /** Thickness of the connector assembly (connector_assembly.thickness, e.g. 22).*/
  assemblyThickness: number;
  /** Total width of assemblies along Z.*/
  assemblyWidth: number;
  /** Assembly height, used by the notch header.*/
  assemblyHeight: number;

  /** Origin of the plane with respect to which the movement occurs*/
  beamsOrigin: Vec3;
  /** Shear of the joist in the X-Y plane; Does not rotate connectors, notches or fasteners.*/
  skewAlphaDeg: number;
  /** Shear of the joist in the X-Z plane (same family as skewAlphaDeg); does not rotate connectors, notches or fasteners.*/
  skewBetaDeg: number;
  /** Connector's distance (mm) below the beams' top face — shifts the header notch down. Default 0.*/
  connectorTopDistance?: number;
  /** Joist only: mm its top sits below the beams' top face (header top). Default 0.*/
  topOffsetMm?: number;
}

/**
 * Fiber axis of the beam in the viewer world (SPEC G8, AC-5):
 * - joist → "x" (fibers along X — horizontal joist beam)
 * - header beam → "z" (HORIZONTAL header beam: fibers along Z)
 * - header column→ "y" (VERTICAL header beam / column: fibers along Y)
 *
 * @param role Role of the beam.
 * @param orientation Orientation of the header beam ("beam" | "column").
 * @returns Fiber axis.*/
export function beamAxisFor(role: BeamRole, orientation: BeamOrientation): BeamAxis {
  if (role === "joist") return "x";
  return orientation === "column" ? "y" : "z";
}

/**
 * Center of the beam in the world (PLAN §2.12, SPEC G8/AC-5).
 *
 * Formulas:
 * - COLUMN HEADER: `center_y = h − H_colonna/2` → the final section of the
 * column remains at the joint plane (SPEC G8).
 * - The OTHERS (header beam, joist): derived from the connector plane with the top
 * plane-aligned + offsetFromTop (config, v1 = 0):
 * `center_y = h + offsetFromTop[role] − dims.h/2`.
 * - x/z = 0: all beams are centered on the origin of the connector plane
 * (G5/C1 flush and offsetFromTop are AC-1 visual calibration TODOs).
 *
 * @param role Role of the beam.
 * @param orientation Orientation of the header beam.
 * @param dims Beam dimensions (use dims.h).
 * @param planeHeight Height h of the connector plane.
 * @returns Center [x, y, z] in world (nominal — alphaDeg applied by pivot).*/
export function beamCenterFor(
  role: BeamRole,
  orientation: BeamOrientation,
  dims: BeamDims,
  beamsOrigin: Vec3,
  skewAlphaDeg = 0,
  skewBetaDeg = 0,
): Vec3 {
  //The `role`/`orientation` parameters are part of the PLAN §2.12 e contract
  // diventano significativi quando offsetFromTop ≠ 0 (G5/C1, TODO calibrazione
  //visual AC-1). In v1 (offset 0) the center formula is identical for beam e
  //column: `center_y = h − dims.h/2`. We refer them explicitly to make
  //the legible and stable intent is the public signature.

  let centerY = 0;
  let centerX = 0;
  let centerZ = beamsOrigin[2];

  switch (role) {
    case "header":
      if (orientation === "column") {
        //COLUMN HEADER: the final section of the column remains at the joint plane.
        centerX = beamsOrigin[0] - dims.h / 2;
        centerY = beamsOrigin[1] - dims.l / 2;
      } else {
        centerX = beamsOrigin[0] - dims.b / 2;
        centerY = beamsOrigin[1] - dims.h / 2;
      }

      break;

    case "joist":
      //The joist is an oblique prism anchored to the face of the connector:
      //L remains the real length, while the free end descends towards -Y
      //(alpha, X-Y plane) and/or towards -Z (beta, X-Z plane — same family,
      //independent axis). The center is that of the deformed prism, not
      //that of the original box.
      const alphaRad = THREE.MathUtils.degToRad(skewAlphaDeg);
      const betaRad = THREE.MathUtils.degToRad(skewBetaDeg);
      centerX = beamsOrigin[0] + (dims.l * Math.cos(alphaRad)) / 2;
      centerY = beamsOrigin[1] - dims.h / 2 - (dims.l * Math.sin(alphaRad)) / 2;
      centerZ = beamsOrigin[2] - (dims.l * Math.cos(alphaRad) * Math.tan(betaRad)) / 2;
      break;
  }

  return [centerX, centerY, centerZ];
}

/**
 * Notch for THIS beam (SPEC G7, PLAN §2.12, AC-3).
 *
 * The CutTarget encodes both the cut beam and the cut axis:
 * - "joist-beam" → cut on JOIST beam, "x" axis
 * - "header-beam" → cut on the horizontal HEADER beam, "z" axis
 * - "header-column" → cut on the vertical HEADER beam (column), "y" axis
 * - "none" → no cut → null
 *
 * If the passed role is NOT the cut beam → null (each beam has its own
 * notch or none). The depth of the cut = `assemblyThickness`
 * (`notchDepthFor`, e.g. 22). `surfaceOffset` represents the offset of the
 * anchor surface (STEP 6 → Δshift separated, consumed by render).
 *
 * @param cutTarget Cut target fixed.
 * @param role Role of the beam for which the notch is calculated.
 * @param assemblyThickness Thickness of the connector assembly (mm).
 * @param joistSection Section of the joist (height + alpha skew), used for the
 * joist cut height — see the `height` comment below.
 * @returns NotchSpec if this beam is cut, null otherwise.*/
export function resolveNotch(
  cutTarget: CutTarget,
  role: BeamRole,
  assemblyThickness: number,
  assemblyWidth: number,
  assemblyHeight: number,
  joistSection: { heightMm: number; alphaDeg: number },
  connectorTopDistance = 0,
): NotchSpec | null {
  if (cutTarget === "none") return null;

  //Determines the axis of the cut from the target (independent of orientation:
  //the "header-beam"/"header-column" target already encodes it).
  let axis: BeamAxis;
  switch (cutTarget) {
    case "joist-beam":
      axis = "x";
      break;
    case "header-beam":
      axis = "z";
      break;
    case "header-column":
      axis = "y";
      break;
    default:
      //Forward-compat: unknown future targets → no cut.
      return null;
  }

  //If this role is not the cut beam → no notch for THIS beam.
  const targetRole: BeamRole = cutTarget === "joist-beam" ? "joist" : "header";
  if (role !== targetRole) return null;

  const depth = notchDepthFor(cutTarget, assemblyThickness);
  //Joist cut height (30-09-2026, user request: "il taglio dovrebbe essere
  //della stessa altezza del joist"): the joist height — before it was a fixed
  //1000 mm cutter, which gave the same visual cut but a meaningless
  //`notch.height` (and a 1000 mm quote). With alpha the joist is SHEARED
  //(`y' = y − x·tan(alpha)`, Beam.tsx), so across the cut depth its section
  //moves by `depth·|tan(alpha)|`: added so no uncut sliver is left at the
  //top/bottom. alpha = 0 → exactly the joist height.
  const joistCutHeight =
    joistSection.heightMm + depth * Math.abs(Math.tan(THREE.MathUtils.degToRad(joistSection.alphaDeg)));
  return {
    target: cutTarget,
    role,
    axis,
    thickness: assemblyThickness,
    depth,
    width: assemblyWidth,
    //Header cut starts at the beam's top face and covers the whole connector:
    //top distance + assembly height. The joist cut spans the whole section.
    height: role === "joist" ? joistCutHeight : connectorTopDistance + assemblyHeight,
    surfaceOffset: depth, //v1: the anchoring surface moves by the depth of the cut
  };
}

/**
 * Δshift NOTCH for ONE resolved beam (SPEC G7, AC-3).
 *
 *STEP 6 of the chain produces this vector as a SEPARATE field
 * (`notchShift` on lock and fastener instances — DOES NOT change position). The rendering
 * applies it by translating the cut side group.
 *
 * cut beam (beam.notch ≠ null)
 * → anchor surface shifted by `surfaceOffset` (mm)
 * → shift along the NORMAL of the anchoring surface
 * (Y-Z connector plane → X direction in viewer, SPEC G1)
 *
 * SIGN — EXPLICIT DECISION (TODO visual calibration AC-1):
 * - Connector plane normal is +X ([1,0,0], `ConnectorPlane.normal`).
 * - We choose POSITIVE shift along X: the anchoring surface moves
 * in the direction of the normal of the plane. If the notch in the visual test
 * must "push" the lock in the opposite direction, reverse the sign here
 * (single source: consumed by resolveScene STEP 6).
 *
 * @param beam BeamSpec resolved (header or joist).
 * @returns Δshift vector to apply to the locks/fasteners on THIS side,
 * or null if the beam is NOT cut (no notch).*/
export function notchShiftFor(beam: BeamSpec): Vec3 | null {
  if (!beam.notch) return null;
  //Direction = normal of the anchoring surface (Y-Z connector plane),
  //i.e. long X in the viewer system. TODO(R_AC3): sign to calibrate in AC-1.

  let localShift = [0, 0, 0] as Vec3;
  switch (beam.notch.target) {
    case "joist-beam":
      localShift = [beam.notch.surfaceOffset, 0, 0];
      break;
    case "header-beam":
    case "header-column":
      localShift = [-beam.notch.surfaceOffset, 0, 0];
      break;
  }

  return localShift;
}

/**
 * Quaternion of the beam AXES (PLAN §2.12, SPEC G8).
 *
 * The beam has the local X axis coinciding with the fibres; we bring it to the world
 * second `axis`:
 * - "x" → identity (local X already long world
 * - "z" → Ry(−90°): local X → world Z (horizontal header beam)
 * - "y" → Rz(+90°): local X → world Y (column)
 *
 * NOTE: DOES NOT include alphaDeg — joist chain rotation (SPEC G6) is the
 * hierarchy pivot in resolveScene.ts (the world quaternion of the beam
 * joist becomes Rz(alphaDeg) ∘ axis automatically).
 *
 * @param axis Axis of fibers in the world.
 * @returns THREE.Quaternion of the axis orientation.*/
export function buildBeamQuaternion(axis: BeamAxis): THREE.Quaternion {
  switch (axis) {
    case "x":
      return new THREE.Quaternion(); // identity (local X fibers already along world X)
    case "z":
      return axisAngleQuat([0, 1, 0], -90); // local X → world Z
    case "y":
      return axisAngleQuat([0, 0, 1], 90); // local X → world Y
    default:
      return new THREE.Quaternion();
  }
}

/**
 * Resolves the NOMINAL BeamSpec of a beam (PLAN §2.12).
 *
 * Composes: axis (beamAxisFor) + center (beamCenterFor) + axis quaternion
 * (buildBeamQuaternion) + notch (resolveNotch). The notch is present only if the
 * CutTarget coincides with this beam (SPEC G7).
 *
 * The alphaDeg rotation of the joist beam is NOT here: it happens in the pivot of the
 * hierarchy (resolveScene) and the final center/quaternion world are extracted
 * from group after updateMatrixWorld.
 *
 * @param input Resolution parameters (BeamInput).
 * @returns BeamSpec NOMINAL (alphaDeg not yet applied).*/
export function resolveBeam(input: BeamInput): BeamSpec {
  const { role, dims, orientation, cutTarget, assemblyThickness, assemblyWidth, assemblyHeight, beamsOrigin, skewAlphaDeg, skewBetaDeg, connectorTopDistance = 0, topOffsetMm = 0 } = input;

  const axis = beamAxisFor(role, orientation);
  const quaternion = buildBeamQuaternion(axis);
  const center = beamCenterFor(role, orientation, dims, beamsOrigin, skewAlphaDeg, skewBetaDeg);
  // The joist top drops below the header top; the whole joist body moves with it.
  if (role === "joist") center[1] -= topOffsetMm;
  const notch = resolveNotch(cutTarget, role, assemblyThickness, assemblyWidth, assemblyHeight, {
    heightMm: dims.h,
    alphaDeg: role === "joist" ? skewAlphaDeg : 0,
  }, connectorTopDistance);

  return {
    role,
    dims,
    axis,
    center,
    quaternion,
    notch,
    skewAlphaDeg: role === "joist" ? skewAlphaDeg : 0,
    skewBetaDeg: role === "joist" ? skewBetaDeg : 0,
  };
}
